/**
 * useGradeSubmission Hook
 *
 * Custom hook for managing grade submissions for a route.
 * Handles fetching the user's submission and submitting/updating grades.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import { getGradeSubmissionId, type VGrade, type GradeSubmission } from '@flash/shared';

/**
 * State interface for grade submission query
 */
interface UseGradeSubmissionState {
  /** The user's current grade submission (null if none) */
  submission: GradeSubmission | null;
  /** Whether data is being loaded */
  loading: boolean;
  /** Error message if query failed */
  error: string | null;
}

/**
 * Return type for useGradeSubmission hook
 */
interface UseGradeSubmissionResult extends UseGradeSubmissionState {
  /** Submit or update a grade for the route */
  submitGrade: (grade: VGrade) => Promise<void>;
  /** Remove the user's grade submission */
  removeGrade: () => Promise<void>;
  /** Whether a submission operation is in progress */
  isSubmitting: boolean;
}

/**
 * Hook to manage grade submissions for a specific route
 *
 * @param routeId - The route ID to manage submissions for
 * @returns Object containing submission state and management functions
 *
 * @example
 * ```tsx
 * const {
 *   submission,
 *   loading,
 *   error,
 *   submitGrade,
 *   removeGrade,
 *   isSubmitting,
 * } = useGradeSubmission('route-123');
 *
 * // Submit a grade
 * await submitGrade('V4');
 *
 * // Remove the submission
 * await removeGrade();
 * ```
 */
export const useGradeSubmission = (routeId: string | undefined): UseGradeSubmissionResult => {
  const { user } = useAuth();

  const [state, setState] = useState<UseGradeSubmissionState>({
    submission: null,
    loading: true,
    error: null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Real-time subscription to user's submission
  useEffect(() => {
    if (!user || !routeId) {
      setState({ submission: null, loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    const db = getFirebaseDb();
    const submissionId = getGradeSubmissionId(user.uid, routeId);
    const submissionRef = doc(db, 'gradeSubmissions', submissionId);

    // Set up real-time listener
    const unsubscribe: Unsubscribe = onSnapshot(
      submissionRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          const submission: GradeSubmission = {
            id: docSnapshot.id,
            odKey: data['odKey'] as string,
            userId: data['userId'] as string,
            routeId: data['routeId'] as string,
            grade: data['grade'] as VGrade,
            createdAt: data['createdAt'],
            updatedAt: data['updatedAt'],
          };
          setState({ submission, loading: false, error: null });
        } else {
          setState({ submission: null, loading: false, error: null });
        }
      },
      (err) => {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch grade submission';
        console.error('Error in grade submission subscription:', err);
        setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
      }
    );

    return () => {
      unsubscribe();
    };
  }, [user, routeId]);

  /**
   * Submit or update a grade for the route
   */
  const submitGrade = useCallback(
    async (grade: VGrade): Promise<void> => {
      if (!user) {
        throw new Error('Must be logged in to submit grade');
      }

      if (!routeId) {
        throw new Error('Route ID is required');
      }

      setIsSubmitting(true);

      try {
        const db = getFirebaseDb();
        const submissionId = getGradeSubmissionId(user.uid, routeId);
        const submissionRef = doc(db, 'gradeSubmissions', submissionId);

        const isNewSubmission = !state.submission;

        const submissionData = {
          id: submissionId,
          odKey: submissionId,
          userId: user.uid,
          routeId,
          grade,
          ...(isNewSubmission
            ? { createdAt: serverTimestamp() }
            : { createdAt: state.submission?.createdAt }),
          updatedAt: serverTimestamp(),
        };

        await setDoc(submissionRef, submissionData, { merge: true });
      } finally {
        setIsSubmitting(false);
      }
    },
    [user, routeId, state.submission]
  );

  /**
   * Remove the user's grade submission
   */
  const removeGrade = useCallback(async (): Promise<void> => {
    if (!user) {
      throw new Error('Must be logged in to remove grade');
    }

    if (!routeId) {
      throw new Error('Route ID is required');
    }

    setIsSubmitting(true);

    try {
      const db = getFirebaseDb();
      const submissionId = getGradeSubmissionId(user.uid, routeId);
      const submissionRef = doc(db, 'gradeSubmissions', submissionId);

      await deleteDoc(submissionRef);
    } finally {
      setIsSubmitting(false);
    }
  }, [user, routeId]);

  return {
    ...state,
    submitGrade,
    removeGrade,
    isSubmitting,
  };
};

export default useGradeSubmission;
